import { TFunction } from 'i18next';
import { Icons } from 'team-hero-ui';

import type { IFormConfig, IRow } from './interfaces/FormConfig';
import { getGenderList } from 'helpers/genderList/genderList.helper';

interface IApplyFormConfigProps {
  t: TFunction;
  showProfilePictureSection: boolean;
  profilePictureRequired?: boolean;
  referralProgramEnabled?: boolean;
}

const getPictureSection = (
  t: TFunction,
  showProfilePictureSection?: boolean,
  profilePictureRequired?: boolean
): IRow[] => {
  if (!showProfilePictureSection) {
    return [];
  }
  return [
    {
      key: 'profilePicture',
      title: t('basics.profilePicture'),
      items: [
        {
          renderType: 'images',
          desktopWidth: 12,
          mobileWidth: 12,
          label: t('component.uploadProfilePicture'),
          propertyName: 'avatar',
          required: profilePictureRequired,
        },
      ],
    },
  ];
};

const getReferralSection = (
  t: TFunction,
  referralProgramEnabled?: boolean
): IRow[] => {
  if (!referralProgramEnabled) {
    return [];
  }
  return [
    {
      key: 'referral',
      items: [
        {
          renderType: 'text',
          label: t('referral.code'),
          propertyName: 'usedReferralCode',
          endAdornment: <Icons.TeamIcon svgSize={45} />,
        },
        {
          renderType: 'helperText',
          label: '',
          helperText: t('referral.codeHelperText'),
          propertyName: '',
        },
      ],
    },
  ];
};

export const applyFormConfig = ({
  t,
  showProfilePictureSection,
  profilePictureRequired,
  referralProgramEnabled,
}: IApplyFormConfigProps): IFormConfig => ({
  sections: [
    {
      key: 'basics',
      rows: [
        {
          key: 'basics_1',
          title: t('basics.mainTitle'),
          items: [
            {
              renderType: 'select',
              propertyName: 'gender',
              label: t('basics.gender'),
              required: true,
              options: getGenderList(t),
              desktopWidth: 3,
              mobileWidth: 6,
            },
            {
              renderType: 'select',
              label: t('basics.title'),
              propertyName: 'title',
              desktopWidth: 3,
              mobileWidth: 6,
              options: [
                { value: 'dr', label: 'Dr.' },
                { value: 'prof', label: 'Prof.' },
              ],
            },
          ],
        },
        {
          key: 'basics_2',
          items: [
            {
              renderType: 'text',
              label: t('basics.firstName'),
              propertyName: 'firstName',
              required: true,
            },
            {
              renderType: 'text',
              label: t('basics.lastName'),
              propertyName: 'lastName',
              required: true,
            },
          ],
        },
        {
          key: 'basics_3',
          items: [
            {
              renderType: 'text',
              label: t('basics.email'),
              propertyName: 'email',
              required: true,
            },
            {
              renderType: 'text',
              label: t('basics.confirmEmail'),
              propertyName: 'confirmEmail',
              required: true,
            },
          ],
        },
        {
          key: 'basics_4',
          items: [
            {
              renderType: 'text',
              label: t('address.addressStreet'),
              propertyName: 'addressStreet',
              required: true,
            },
            {
              renderType: 'text',
              label: t('address.addressZip'),
              propertyName: 'addressZip',
              required: true,
            },
          ],
        },
        {
          key: 'basics_5',
          items: [
            {
              renderType: 'text',
              label: t('address.addressCity'),
              propertyName: 'addressCity',
              required: true,
            },
            {
              renderType: 'selectCountry',
              label: t('address.addressCountry'),
              propertyName: 'addressCountry',
              required: true,
            },
          ],
        },
        {
          key: 'basics_6',
          items: [
            {
              renderType: 'phone',
              label: t('address.phone'),
              propertyName: 'phone',
              mask: '{+}00 000 0000000000',
              required: true,
            },
            {
              renderType: 'date',
              label: t('basics.dateOfBirth'),
              propertyName: 'dateOfBirth',
              required: true,
            },
          ],
        },
        ...getReferralSection(t, referralProgramEnabled),
        ...getPictureSection(
          t,
          showProfilePictureSection,
          profilePictureRequired
        ),
      ],
    },
  ],
});
